import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowRight, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@tabipla/ui";

interface GroupBalance {
  groupId: string;
  groupName: string;
  balance: number;
}

interface SettlementReminderProps {
  totalReceivable: number;
  totalPayable: number;
  groups: GroupBalance[];
}

export function SettlementReminder({
  totalReceivable,
  totalPayable,
  groups,
}: SettlementReminderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Filter groups with outstanding balances
  const groupsToReceive = groups.filter((g) => g.balance > 0);
  const groupsToPay = groups.filter((g) => g.balance < 0);

  const hasOutstanding = totalReceivable > 0 || totalPayable > 0;

  if (!hasOutstanding) return null;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(Math.abs(amount));
  };

  // Show the most urgent reminder (pay first, then receive)
  const urgentGroup = groupsToPay[0] ?? groupsToReceive[0];
  const isPayment = urgentGroup && urgentGroup.balance < 0;

  return (
    <Card
      className={`cursor-pointer border-2 transition-all hover:shadow-md active:scale-[0.99] ${
        isPayment
          ? "border-amber-300 bg-gradient-to-r from-amber-50 to-white dark:border-amber-700 dark:from-amber-950/30 dark:to-gray-900"
          : "border-emerald-300 bg-gradient-to-r from-emerald-50 to-white dark:border-emerald-700 dark:from-emerald-950/30 dark:to-gray-900"
      }`}
      onClick={() => urgentGroup && navigate(`/groups/${urgentGroup.groupId}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              isPayment
                ? "bg-amber-100 dark:bg-amber-900/50"
                : "bg-emerald-100 dark:bg-emerald-900/50"
            }`}
          >
            {isPayment ? (
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            ) : (
              <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">
                {isPayment
                  ? t("reminder.needToPay", "精算待ちがあります")
                  : t("reminder.canReceive", "受け取れるお金があります")}
              </h3>
            </div>

            {urgentGroup && (
              <p className="mt-1 text-sm text-muted-foreground">
                {isPayment ? (
                  <>
                    「{urgentGroup.groupName}」で{" "}
                    <span className="font-semibold text-amber-600 dark:text-amber-400">
                      {formatAmount(urgentGroup.balance)}
                    </span>{" "}
                    を支払う
                  </>
                ) : (
                  <>
                    「{urgentGroup.groupName}」で{" "}
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatAmount(urgentGroup.balance)}
                    </span>{" "}
                    を受け取れます
                  </>
                )}
              </p>
            )}

            {/* Additional pending items */}
            {(groupsToPay.length > 1 || groupsToReceive.length > 1) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {groupsToPay.length > 1 && (
                  <span>
                    他{groupsToPay.length - 1}件の支払い待ち
                    {groupsToReceive.length > 0 && " / "}
                  </span>
                )}
                {groupsToReceive.length > (isPayment ? 0 : 1) && (
                  <span>{groupsToReceive.length - (isPayment ? 0 : 1)}件の受け取り待ち</span>
                )}
              </p>
            )}
          </div>

          {/* Arrow */}
          <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}
